import { useStore } from '@nanostores/react';
import { type RegularTokenBreakdown, getTokenData, calculateSafePercentage } from './utils';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';

interface BarProps {
  warning?: boolean;
  tokens: RegularTokenBreakdown | number;
  barClassName?: string;
  maxTokens: number;
  isFreeTier?: boolean;
}

export const TokenConsumptionBar = (props: BarProps) => {
  const { warning = false, tokens, barClassName, maxTokens, isFreeTier } = props;
  const user = useStore(userStore.user);

  const isDowngradedToFree = user?.membership?.subscription?.status === 'canceled' && user?.membership?.tier >= 1;

  const tokenData = getTokenData({ tokens, maxTokens, isFreeTier, isDowngradedToFree });

  const isExtraTokens = typeof tokens === 'number';
  const isMonthlyTokens = typeof tokens === 'object' && tokens.type === 'active-month';

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs flex gap-2 flex-wrap">
        <span className="font-semibold">{tokenData.count}</span>
        <span className="text-bolt-ds-textSecondary">{tokenData.description}</span>
      </p>
      <div
        className={classNames(
          'h-2 rounded relative',
          {
            'bg-bolt-ds-brand/10': isMonthlyTokens,
            'bg-bolt-ds-brand': !isFreeTier && !isMonthlyTokens,
            'bg-bolt-ds-warning/10': warning,
            'bg-bolt-ds-success': isExtraTokens,
          },
          barClassName,
        )}
        style={
          isMonthlyTokens ? { width: `${tokenData.widthMaxCapacity}%` } : { width: `${tokenData.widthPercentage}%` }
        }
        role="progressbar"
      >
        {isMonthlyTokens && tokenData.leftTokens > 0 && (
          <div
            className={classNames('absolute h-2 rounded', warning ? 'bg-bolt-ds-warning' : 'bg-bolt-ds-brand')}
            style={{ width: `${tokenData.widthMonthlyToCapacity}%` }}
          />
        )}
      </div>
    </div>
  );
};

type TokenConsumptionCompactBar = {
  tokens: {
    total: number;
    left: number;
  };
  warning?: boolean;
  extraTokens: number;
};

export const TokenConsumptionCompactBar = ({
  warning = false,
  extraTokens = 0,
  tokens,
}: TokenConsumptionCompactBar) => {
  const totalTokens = tokens.total + extraTokens;
  const extraTokensPercentage = calculateSafePercentage(extraTokens, totalTokens);
  const usedTokensPercentage = calculateSafePercentage(tokens.left, totalTokens);

  return (
    <div className={classNames('w-full h-2 rounded', 'flex items-center gap-0.5')} role="progressbar">
      {extraTokens > 0 && (
        <div
          className="h-2 rounded bg-bolt-ds-success"
          style={{ width: `${extraTokensPercentage}%` }}
          aria-label={`Extra tokens: ${extraTokens}`}
        />
      )}

      <div
        className={classNames(
          'flex items-center justify-start h-2 grow shrink-0 rounded',
          warning ? 'bg-bolt-ds-warningContainer/10' : 'bg-bolt-ds-brandContainer/10',
        )}
      >
        <div
          className={classNames('h-2 rounded', warning ? 'bg-bolt-ds-warning' : 'bg-bolt-ds-brand')}
          style={{ width: `${usedTokensPercentage}%` }}
        />
      </div>
    </div>
  );
};
